import { ContentItem } from "../types/journey";
import { JourneyContentRow, GameName } from "../types/games";
import { THIS_OR_THAT, TRIVIA, PICTURE_PUZZLE } from "../constants";

/**
 * Converts ContentItem[] (server/persistence format) to ContentRow[] (UI editing format)
 * This standardizes the conversion that was previously duplicated across components
 */
export const contentItemsToRows = (
  items: ContentItem[] = [],
): JourneyContentRow[] => {
  if (!items || items.length === 0) {
    return [{ id: Date.now() }]; // Always provide at least one empty row
  }

  return items.map((item) => {
    const rowId =
      typeof item.id === "string" || item.id === undefined
        ? Date.now() + Math.floor(Math.random() * 10000)
        : Number(item.id);

    // Start with common fields for all types
    const baseRow: JourneyContentRow = {
      id: rowId,
      touchpoint_id: item.touchpoint_id,
    };

    switch (item.game_type) {
      case THIS_OR_THAT:
        return {
          ...baseRow,
          choice_1: item.choice_1,
          choice_2: item.choice_2,
        };

      case TRIVIA:
        // Parse options array into individual option fields for the UI
        const optionFields: Record<string, string> = {};
        if (item.options && item.options.length > 0) {
          item.options.forEach((option, index) => {
            optionFields[`option${index + 1}`] = option;
          });
        }

        return {
          ...baseRow,
          question: item.question,
          answer: item.answer,
          options: item.options,
          ...optionFields,
        };

      case PICTURE_PUZZLE:
        return {
          ...baseRow,
          answer: item.answer,
          clue: item.clue,
          image_id: item.image_id,
        };

      default:
        return baseRow;
    }
  });
};

/**
 * Converts ContentRow[] (UI editing format) to ContentItem[] (server/persistence format)
 * Filters for only valid rows based on game type and returns properly formatted data
 */
export const rowsToContentItems = (
  rows: JourneyContentRow[] = [],
  gameName: GameName,
  touchpointId?: string,
): ContentItem[] => {
  if (!rows || rows.length === 0 || !gameName) {
    return [];
  }

  // Map game name to game type constant
  const gameType = getGameTypeFromName(gameName);

  // Filter for valid rows based on game type
  const validRows = rows.filter((row) => {
    if (!row) return false;

    // Basic validation
    if (gameName === THIS_OR_THAT) {
      if (!row.choice_1 || !row.choice_2) return false;
    } else if (gameName === TRIVIA) {
      if (!row.question || !row.answer) return false;
      // Check for options either as array or individual fields
      if (
        !row.options?.length &&
        !Object.keys(row).some((key) => key.startsWith("option"))
      ) {
        return false;
      }
    } else if (gameName === PICTURE_PUZZLE) {
      if (!row.answer || !row.image_id) return false;
    }

    return true;
  });

  // Convert to ContentItem format
  return validRows.map((row) => {
    // Start with common fields
    const baseItem: ContentItem = {
      id: row.id,
      touchpoint_id: touchpointId,
      game_type: gameType, // Use the constant to ensure type safety
    };

    if (gameName === THIS_OR_THAT) {
      return {
        ...baseItem,
        choice_1: row.choice_1 || "",
        choice_2: row.choice_2 || "",
      };
    } else if (gameName === TRIVIA) {
      // Collect options from individual option fields
      const options =
        row.options ||
        [1, 2, 3, 4].map((num) => row[`option${num}`] || "").filter(Boolean);

      return {
        ...baseItem,
        question: row.question || "",
        answer: row.answer || "",
        options: options,
      };
    } else if (gameName === PICTURE_PUZZLE) {
      return {
        ...baseItem,
        answer: row.answer || "",
        clue: row.clue || "",
        image_id: row.image_id,
      };
    }

    // This case only happens if gameName doesn't match any known game
    // Adding a default game_type prevents type errors
    return baseItem;
  });
};

/**
 * Helper function to get the game type constant from the game name
 */
function getGameTypeFromName(gameName: GameName): string {
  switch (gameName) {
    case THIS_OR_THAT:
      return THIS_OR_THAT;
    case TRIVIA:
      return TRIVIA;
    case PICTURE_PUZZLE:
      return PICTURE_PUZZLE;
    default:
      // Fallback to prevent undefined game_type
      return "UNKNOWN_GAME_TYPE";
  }
}
